<template>
  <LoadingIndicator
    absolute
    v-if="!teiData"
  />
  <TEIDataError
    v-else-if="teiData.error || configError"
    :message="teiData.error || configError"
  />
  <TEILayoutContainer
    v-else
    :show-check-button="showCheckButton"
    :show-checking-button="submittingAttempt"
    :show-reset-button="showCheckButton"
    :show-try-again-button="false"
    :check-button-enabled="checkButtonEnabled"
    :reset-button-enabled="checkButtonEnabled"
    :scoring-bar="{
      attempts: (showFeedback || updatingAttemptItemTry) ? tries : tries + 1,
      maxAttempts: maxTries,
      showTries: showTries,
      score: maxScore,
    }"
    :show-scoring-bar="!showFeedback"
    :show-response-bar="showFeedback"
    :response-bar="{
      state: 'correct',
      content: $t('Your answer has been submitted to your teacher.'),
      correctPrependMessage: $t('Great job!'),
    }"
    :is-displayed="isDisplayed"
    :check-button-text="$t('Submit')"
    :checking-button-text="$t('Submitting...')"
    :asset-id="assetId"
    @reset-button-clicked="onResetClicked"
    @check-button-clicked="onCheckClicked"
  >
    <div
      class="constructed-response-tei"
    >
      <TEIInstructionsCard
        position="top"
        class="constructed-response-tei__instructions-card"
        :content="promptContent"
        do-sticker-position="bottom-left"
        :image-url="promptImageUrl"
        :image-alt="promptImageAlt"
        :max-lines="3"
      />
      <div
        class="constructed-response-tei__answer-field"
        :class="{
          'constructed-response-tei__answer-field--focused': answerFieldFocused,
          'constructed-response-tei__answer-field--disabled': !answerFieldEnabled,
        }"
        @click="onAnswerFieldClicked"
      >
        <span class="nebula-screenreader-only">
          {{ screenreaderText }}
        </span>
        <StudioTextEditor
          ref="answerFieldTextEditor"
          class="constructed-response-tei__answer-field-text-editor"
          :id="answerFieldId"
          :content="answerHtml"
          :disabled="!answerFieldEnabled"
          data-type="html"
          mode="EDIT"
          overflow="scroll"
          :floating-menu="false"
          :scale="scale"
          :show-empty-styles="false"
          :disable-icons="true"
          :disable-modal-link-option="true"
          :disable-link="true"
          :toolbar-enabled="false"
          :default-text-placeholder="$t('Enter Text Here...')"
          :aria-label="$t('Answer input box')"
          @update="onAnswerHtmlChanged"
          @focus="answerFieldFocused = true"
          @blur="answerFieldFocused = false"
        />
      </div>
    </div>
  </TEILayoutContainer>
</template>

<script>
import { v4 as uuid } from 'uuid';
import {
  get, some, first, isEmpty,
} from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import TEILayoutContainer from '@/components/teis/TEILayoutContainer.vue';
import TEIDataError from '@/components/teis/TEIDataError.vue';
import TEIInstructionsCard from '@/components/teis/TEIInstructionsCard.vue';
import StudioTextEditor from '@/components/StudioTextEditor.vue';
import fullSlideTEI from '@/mixins/fullSlideTEI';
import modals from '@/mixins/modals';
import { extractHtml, stripTags } from '@/lib/utils';
import {
  TEI_CHECKING_ANSWERS_MODAL_DELAY,
  TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS,
} from '@/lib/constants';
import {
  useAssessmentStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'ConstructedResponseTEI',
  components: {
    LoadingIndicator,
    TEILayoutContainer,
    TEIDataError,
    TEIInstructionsCard,
    StudioTextEditor,
  },
  mixins: [fullSlideTEI, modals],
  data() {
    return {
      answerFieldId: uuid(),
      answerHtml: null,
      answerDoc: null,
      checkingAnswersModalId: 'TEICheckingAnswersModal',
      answerFieldFocused: false,
      maxTries: 1,
      updatingAttemptItemTry: false,
    };
  },
  async created() {
    this.answerHtml = this.mapResponseToAnswer(this.attemptItemTry?.response);

    // If we've already submitted once before, show the feedback immediately
    this.showFeedback = !!this.userCanEditAttempt && !this.hasTriesRemaining;
  },
  watch: {
    attemptItemTry(newTry) {
      // if we've just cleared answers
      if (this.attemptIsClearing && !newTry.tries && this.isDisplayed) {
        this.submittingAttempt = false;
        this.showFeedback = !!newTry.tries;

        // clear out the text content
        this.onResetClicked();

        // set attempt cleared
        this[types.SET_ATTEMPT_CLEARING](false);
      }

      // This watcher is triggered when a response comes back from the api
      if (this.checkAnswersModal) {
        // Make sure the try number doesn't flicker between renders
        this.updatingAttemptItemTry = true;

        this.checkAnswersModal.close(() => {
          this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
          this.submittingAttempt = false;
          this.showFeedback = !this.hasTriesRemaining;
          this.updatingAttemptItemTry = false;
        });
      }
    },
    answerIsEmpty() {
      if (this.answerIsEmpty) {
        this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
      }
    },
  },
  computed: {
    ...mapState(useAssessmentStore, [
      'attemptIsClearing',
    ]),
    hasTriesRemaining() {
      return this.attemptItemTry?.tries < this.maxTries;
    },
    answerFieldEnabled() {
      return !!(this.userCanEditAttempt
        && !this.showFeedback
        && !this.submittingAttempt
        && this.hasTriesRemaining);
    },
    configError() {
      const uiLayout = this.teiData?.uiLayout;
      if (uiLayout?.type !== 'flow_layout') {
        return this.$t('Error: Only Flow/Fill in the Blank layout is currently supported');
      }
      const responseFieldCount = this.teiData?.responseFields?.length;
      if (responseFieldCount !== 1) {
        return this.$t('Error: TEI should have exactly one response field');
      }
      const elementCount = get(this, 'teiData.responseFields[0].elements.length');
      if (elementCount !== 1) {
        return this.$t('Error: TEI response field should have exactly one element');
      }
      const fieldType = get(this, 'teiData.responseFields[0].elements[0].type');
      if (fieldType !== 'textBox') {
        return this.$t('Error: Input field should be a text box');
      }

      return null;
    },
    promptImageUrl() {
      return this.teiData.prompt.lang[this.teiLang]?.resource?.[0]?.url;
    },
    promptImageAlt() {
      return this.teiData.prompt.lang[this.teiLang]?.resource?.[0]?.title;
    },
    promptContent() {
      const prompt = this.teiData?.prompt?.lang?.[this.teiLang]?.text;
      return extractHtml(prompt, new Set(TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS));
    },
    showCheckButton() {
      return !this.showFeedback && !this.submittingAttempt;
    },
    checkButtonEnabled() {
      return !!(!this.answerIsEmpty
        && this.userCanEditAttempt
        && !this.submittingAttempt);
    },
    answerIsEmpty() {
      return !this.answerDoc || !this.nodeHasTextContent(this.answerDoc);
    },
    showTries() {
      return !!(this.attemptItemTry && !isEmpty(this.attemptItemTry));
    },
    screenreaderText() {
      return this.answerIsEmpty
        ? this.$t('Answer input box is empty')
        : this.$t('%(answerText)s', {
          answerText: stripTags(this.answerHtml),
        });
    },
  },
  methods: {
    ...mapActions(useAssessmentStore, [
      types.SUBMIT_RESPONSE,
      types.UPDATE_UNSUBMITTED_ATTEMPT,
      types.SET_ATTEMPT_CLEARING,
    ]),
    onAnswerHtmlChanged({ html, doc }) {
      this.answerHtml = html;
      this.answerDoc = doc;
      this[types.UPDATE_UNSUBMITTED_ATTEMPT](true);
    },
    onCheckClicked() {
      this.checkAnswersModal = this.openModalDeferred(TEI_CHECKING_ANSWERS_MODAL_DELAY, {
        id: this.checkingAnswersModalId,
        type: this.checkingAnswersModalId,
      });
      this.submittingAttempt = true;
      this[types.SUBMIT_RESPONSE]({
        teiId: this.assetId,
        payload: this.mapAnswerToResponse(),
      });
    },
    onResetClicked() {
      this.answerHtml = null;
      this.answerDoc = null;
      this.$nextTick(() => {
        this.$refs.answerFieldTextEditor.updateEditorContent();
      });
    },
    nodeHasTextContent(node) {
      if (node.type === 'text' && ((node.text || '').trim()).length > 0) {
        return true;
      }
      if (some(node.content, this.nodeHasTextContent)) {
        return true;
      }
      return false;
    },
    mapAnswerToResponse() {
      return {
        answerFieldData: [this.answerHtml],
      };
    },
    mapResponseToAnswer(response) {
      const html = first(response?.answerFieldData);
      if (!html) return null;
      if (html.includes('studio-tei-answer-wrapper')) {
        const el = document.createElement('div');
        el.innerHTML = html;
        return el.firstChild.innerHTML;
      }
      return html;
    },
    onAnswerFieldClicked(event) {
      if (event.target !== this.$refs?.answerFieldTextEditor?.$el) return;
      const editor = this.$refs?.answerFieldTextEditor?.editor;
      if (editor) editor.focus();
    },
  },
};
</script>

<style lang="stylus">
.constructed-response-tei {
  @import './styles/tei-fonts';

  display: flex;
  flex-direction: column;
  height: 100%;

  &__instructions-card {
    $instructions-card-height = 144px;
    height: $instructions-card-height;
    margin: 0 $nebula-space-4x $nebula-space-2x;
    max-height: $instructions-card-height;
    min-height: $instructions-card-height;
  }

  &__answer-field {
    background: $nebula-color-white;
    box-shadow: var(--tei-cr-answer-field-box-shadow);
    border-radius: $nebula-border-radius-large;
    flex: 1 1 auto;
    margin: $nebula-space-2x $nebula-space-4x $nebula-space-7x $nebula-space-4x;
    overflow: hidden;

    .text-editor__content * {
      tei-mikado-body-primary-regular();
    }

    &--focused {
      &:not(.constructed-response-tei__answer-field--disabled) {
        box-shadow: var(--tei-cr-answer-field-focused-box-shadow);

        .text-editor__is-empty {
          color: var(--tei-cr-answer-field-placeholder-active-color);
        }
      }
    }

    &--disabled {
      background-color: var(--tei-cr-answer-field-disabled-color);
      box-shadow: none;

      .text-editor__content * {
        color: var(--tei-cr-answer-field-disabled-text-color);
      }
    }
  }

  &__answer-field-text-editor {
    border-width: 0;
    height: 100%;
    padding: $nebula-space-3x $nebula-space-1x $nebula-space-3x $nebula-space-3x;

    .text-editor__is-empty {
      color: var(--tei-cr-answer-field-disabled-text-color);
    }
  }
}
</style>
